import { ReducerError } from '@/errors/ReducerError';
import { MailingStatus } from '@/types';
import { FilterCriteria } from '@/filterCriteria/types';
import { Reducer } from 'react';
import type { Action } from './actions';
import { DEFAULT_TASK_CHOICES } from './initialState';

export const filterCriteriaReducer: Reducer<FilterCriteria, Action> = (state, action) => {
  switch (action.type) {
    case 'setGeometries':
      return { ...state, geometries: action.payload };
    case 'setLocationOperator':
      return { ...state, locationOperator: action.payload };
    case 'setGroups':
      return { ...state, groups: action.payload };
    case 'setTags':
      return { ...state, tags: action.payload };
    case 'setContactable':
      return { ...state, contactable: action.payload };
    case 'setLists':
      return { ...state, lists: action.payload };
    case 'setStatuses':
      return { ...state, statuses: action.payload };
    case 'setEventTypes':
      return { ...state, eventTypes: action.payload };
    case 'setEvents':
      return { ...state, events: action.payload };
    case 'setEventCampaigns':
      return { ...state, eventCampaigns: action.payload };
    case 'setElection':
      // This is a special case, as we need to check if there is a change in
      // election, then electorates are cleared as well.

      if (action.payload && state.election && action.payload.label === state.election.label) {
        return { ...state, election: action.payload };
      } else if (
        action.payload &&
        state.election &&
        action.payload.label !== state.election.label
      ) {
        return { ...state, election: action.payload, electorates: [] };
      }
      return {
        ...state,
        election: action.payload,
        electorates: [],
        rostertypes: [{ id: 1, label: 'Rostered' }],
        rostercriteria: { votingPeriod: 'Any', shiftStatus: 'Confirmed' },
      };
    case 'setEventDateRange':
      return { ...state, eventDateRange: action.payload };
    case 'setElectorates':
      return { ...state, electorates: action.payload };
    case 'setRosterTypes':
      return { ...state, rostertypes: action.payload };
    case 'setRosterCriteria':
      return { ...state, rostercriteria: action.payload };
    case 'setMemberships':
      if (action.payload.length === 0) {
        return { ...state, memberships: [], branches: [], notRenewing: '' };
      }
      return { ...state, memberships: action.payload };
    case 'setBranches':
      return { ...state, branches: action.payload };
    case 'setNotRenewing':
      return { ...state, notRenewing: action.payload };
    case 'setSupporters':
      return { ...state, supporterstatuses: action.payload };
    case 'setTaskElections':
      if (action.payload.length === 0) {
        return {
          ...state,
          taskelections: action.payload,
          issincespecificelection: false,
          taskchoices: [],
        };
      } else if (
        action.payload.length === 1 &&
        state?.taskelections &&
        state.taskelections.length === 0
      ) {
        return {
          ...state,
          taskelections: action.payload,
          taskchoices: DEFAULT_TASK_CHOICES,
          taskkinds: [],
          tasknote: '',
        };
      }

      return { ...state, taskelections: action.payload };
    case 'setIsSinceSpecificElection':
      return { ...state, issincespecificelection: action.payload };
    case 'setTaskChoices':
      return { ...state, taskchoices: action.payload };
    case 'setTaskKinds':
      return { ...state, taskkinds: action.payload };
    case 'setTaskNote':
      return { ...state, tasknote: action.payload };
    case 'setMailings':
      if (action.payload.length === 1 && state?.mailings && state.mailings.length === 0) {
        return {
          ...state,
          mailings: action.payload,
          mailingstatus: 'delivered',
        };
      } else if (action.payload.length === 0) {
        return {
          ...state,
          mailings: action.payload,
          mailingstatus: undefined,
        };
      }
      return { ...state, mailings: action.payload };
    case 'setMailingStatus':
      return { ...state, mailingstatus: action.payload as MailingStatus };
    case 'setActivities':
      if (action.payload.length === 0) {
        return {
          ...state,
          activities: action.payload,
          activityStatuses: [],
          activityDateRange: {
            displayedStartDate: null,
            displayedEndDate: null,
            periodValue: 'bytimeperiod',
            periodDetails: 'lastweek',
          },
          subject: '',
        };
      } else if (
        action.payload.length === 1 &&
        state?.activities &&
        state.activities.length === 0
      ) {
        return {
          ...state,
          activities: action.payload,
          activityStatuses: [],
          activityDateRange: {
            displayedStartDate: null,
            displayedEndDate: null,
            periodValue: 'bytimeperiod',
            periodDetails: 'lastweek',
          },
          subject: '',
        };
      }
      return { ...state, activities: action.payload };
    case 'setCallingStatuses':
      if (action.payload.length === 0) {
        return {
          ...state,
          callingstatusinclusion: undefined,
          callingstatuses: [],
          callingstatusDateRange: {
            displayedStartDate: null,
            displayedEndDate: null,
            callingPeriodValue: 'withinlast',
            callingPeriodDetails: 'weeks',
            callingPeriodQuantity: 3,
          },
          callingstatusLists: [],
          callingstatusUsers: [],
          callingstatusCampaigns: [],
        };
      } else if (
        action.payload.length === 1 &&
        state?.callingstatuses &&
        state.callingstatuses.length === 0
      ) {
        return {
          ...state,
          callingstatuses: action.payload,
          callingstatusinclusion: false,
          callingstatusDateRange: {
            displayedStartDate: null,
            displayedEndDate: null,
            callingPeriodValue: 'withinlast',
            callingPeriodDetails: 'weeks',
            callingPeriodQuantity: 3,
          },
          callingstatusLists: [],
          callingstatusUsers: [],
          callingstatusCampaigns: [],
        };
      } else {
        return { ...state, callingstatuses: action.payload };
      }
    case 'setCallingStatusLists':
      return { ...state, callingstatusLists: action.payload };
    case 'setCallingStatusUsers':
      return { ...state, callingstatusUsers: action.payload };
    case 'setCallingStatusCampaigns':
      return { ...state, callingstatusCampaigns: action.payload };
    case 'setActivityStatuses':
      return { ...state, activityStatuses: action.payload };
    case 'setActivityDateRange':
      return { ...state, activityDateRange: action.payload };
    case 'resetActivityDateRange':
      return {
        ...state,
        activityDateRange: {
          startDate: null,
          endDate: null,
          displayedStartDate: null,
          displayedEndDate: null,
          periodValue: 'bytimeperiod',
          periodDetails: 'lastweek',
        },
      };
    case 'setSubject':
      return { ...state, subject: action.payload };
    case 'setCallingStatusInclusion':
      return { ...state, callingstatusinclusion: action.payload };
    case 'setCallingStatusDateRange':
      return { ...state, callingstatusDateRange: action.payload };
    case 'resetCallingStatusDateRange':
      return {
        ...state,
        callingstatusDateRange: {
          displayedStartDate: null,
          displayedEndDate: null,
          callingPeriodValue: 'withinlast',
          callingPeriodDetails: 'weeks',
          callingPeriodQuantity: 3,
        },
      };
    case 'setAge':
      return { ...state, age: action.payload };
    case 'initialise':
      return {
        ...state,
        ...action.payload,
      };

    case 'setPathwaySteps':
      return { ...state, pathwaySteps: action.payload };

    case 'setCommsPreferences':
      if (action.payload.length === 0) {
        return {
          ...state,
          commspreferencesinclusion: undefined,
          commspreferences: [],
        };
      } else if (
        action.payload.length === 1 &&
        state?.commspreferences &&
        state.commspreferences.length === 0
      ) {
        return {
          ...state,
          commspreferences: action.payload,
          commspreferencesinclusion: false,
        };
      } else {
        return { ...state, commspreferences: action.payload };
      }

    case 'setCommsPreferencesInclusion':
      return { ...state, commspreferencesinclusion: action.payload };

    default:
      // This should never happen, as action.type is of type Action. But just in
      // case

      throw new ReducerError('type not defined');
  }
};
